/**
 * @file Emergency.jsx
 * @description Emergency services page with Tailwind CSS
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { api } from '../services/api';

const Emergency = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    serviceType: '',
    description: ''
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [errors, setErrors] = useState({});
  const [validatingPostcode, setValidatingPostcode] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const emergencies = [
    { icon: '💧', title: 'Burst Pipes', desc: 'Rapid response to prevent water damage' },
    { icon: '🔥', title: 'Gas Leaks', desc: 'Immediate Gas Safe engineer dispatch' },
    { icon: '🚿', title: 'No Hot Water', desc: 'Emergency boiler repairs' },
    { icon: '🚽', title: 'Blocked Drains', desc: 'Fast drain clearance service' },
    { icon: '❄️', title: 'Heating Failures', desc: 'Emergency heating restoration' },
    { icon: '⚠️', title: 'Water Leaks', desc: 'Leak detection and repair' }
  ];

  const emergencyPricing = {
    standard: { rate: '£80-120/hr', zone1: '+£20', desc: 'Weekdays 8am-6pm' },
    evening: { rate: '£100-150/hr', zone1: '+£25', desc: 'Weekdays 6pm-10pm' },
    weekend: { rate: '£120-180/hr', zone1: '+£30', desc: 'Weekends 8am-10pm' },
    emergency: { rate: '£150-200/hr', zone1: '+£40', desc: '24/7 Emergency Callout' }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };

    // Live validation on blur
    switch (name) {
      case 'email':
        if (value && !validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (value && !validatePhone(value)) {
          newErrors.phone = 'Please enter a valid UK phone number';
        }
        break;
      case 'postcode':
        if (value) {
          const isValid = await validatePostcode(value);
          if (!isValid) {
            newErrors.postcode = 'Please enter a valid UK postcode';
          }
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/\s/g, '');
    return cleanPhone.length >= 10 && /^[\d\s\+\(\)]+$/.test(phone);
  };

  const validatePostcode = async (postcode) => {
    if (!postcode) return false;
    
    setValidatingPostcode(true);
    try {
      const cleanPostcode = postcode.replace(/\s/g, '').toUpperCase();
      const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}/validate`);
      const data = await response.json();
      setValidatingPostcode(false);
      return data.result === true;
    } catch (error) {
      setValidatingPostcode(false);
      console.error('Postcode validation error:', error);
      return false;
    }
  };

  const validateForm = async () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid UK phone number';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    } else {
      const isValidPostcode = await validatePostcode(formData.postcode);
      if (!isValidPostcode) {
        newErrors.postcode = 'Please enter a valid UK postcode';
      }
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select service type';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Please describe the emergency';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    const isValid = await validateForm();
    if (!isValid) {
      setStatus({
        type: 'error',
        message: 'Please correct the errors below and try again.'
      });
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
      return;
    }

    setStatus({ type: 'loading', message: 'Sending emergency request...' });

    try {
      // Prepare data for emergency request API
      const requestData = {
        ...formData,
        urgency: 'Emergency' // Emergency requests are always urgent
      };
      
      await api.submitEmergencyRequest(requestData);
      setStatus({
        type: 'success',
        message: 'Emergency request received! An engineer will call you within 15 minutes.'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        postcode: '',
        serviceType: '',
        description: ''
      });
      setErrors({});
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to submit request. Please call us directly at 07379 787766.'
      });
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  return (
    <div className="emergency-page">
      <Helmet>
        <title>Emergency Services | Metro Plumbing & Heating</title>
        <meta
          name="description"
          content="24/7 emergency plumbing, gas, and heating services in London. Rapid response for burst pipes, gas leaks, boiler breakdowns."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute w-96 h-96 bg-white rounded-full top-0 right-0 animate-pulse-slow"></div>
          <div className="absolute w-64 h-64 bg-white rounded-full bottom-0 left-0 animate-bounce-slow"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
            <div className="text-6xl mb-6 animate-bounce relative inline-block">
              <span className="absolute inset-0 animate-ping text-red-300">🚨</span>
              <span className="relative">🚨</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">24/7 Emergency Service</h1>
            <p className="text-xl text-red-100 mb-8">
              Rapid response for plumbing, gas, and heating emergencies across London
            </p>
            <a
              href="tel:+447379787766"
              className="inline-flex items-center space-x-3 px-10 py-5 bg-white text-red-600 rounded-lg font-bold text-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 animate-pulse-slow"
            >
              <span>📞</span>
              <span>07379 787766</span>
            </a>
            <p className="mt-6 text-red-100 text-lg">Engineers on standby - Call now for immediate assistance</p>
          </div>
        </div>
      </section>

      {/* Emergency Types */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">We Handle All Emergencies</h2>
            <p className="text-lg text-gray-600">Fast response times for urgent plumbing, gas, and heating issues</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {emergencies.map((emergency, index) => (
              <div
                key={index}
                className="card text-center p-8 border-2 border-red-200 hover:border-red-400 animate-fade-in-up relative overflow-hidden group hover:scale-105 transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="text-5xl mb-4 animate-bounce-slow">{emergency.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{emergency.title}</h3>
                <p className="text-gray-600">{emergency.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Request Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="card p-8 border-2 border-red-300 animate-fade-in-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Emergency Request Form</h2>
              <p className="text-center text-gray-600 mb-8">
                Submit your emergency details and we'll call you back within 15 minutes
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`input-field ${errors.name ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="John Smith"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                {/* <div>
                  <label className="block text-gray-700 font-semibold mb-2">Contact Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`input-field ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="07379 787766"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div> */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        className={`input-field ${errors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                        placeholder="john@example.com"
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        className={`input-field ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
                        placeholder="07379 787766"
                      />
                      {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                    </div>
                  </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Address *</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className={`input-field ${errors.address ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="123 High Street"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Postcode *</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      className={`input-field ${errors.postcode ? 'border-red-500 focus:border-red-500' : ''}`}
                      placeholder="SW1A 1AA"
                    />
                    {validatingPostcode && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      </div>
                    )}
                  </div>
                  {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                  <p className="text-gray-500 text-sm mt-1">We'll validate your postcode automatically</p>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Emergency Type *</label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className={`input-field ${errors.serviceType ? 'border-red-500 focus:border-red-500' : ''}`}
                  >
                    <option value="">Select service type...</option>
                    {emergencies.map((e) => (
                      <option key={e.title} value={e.title}>
                        {e.title}
                      </option>
                    ))}
                  </select>
                  {errors.serviceType && <p className="text-red-500 text-sm mt-1">{errors.serviceType}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Emergency Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    className={`input-field resize-none ${errors.description ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="Please describe the emergency in detail..."
                  ></textarea>
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>

                <button
                  type="submit"
                  disabled={status.type === 'loading'}
                  className="w-full px-8 py-4 bg-red-600 text-white rounded-lg font-bold text-lg shadow-lg hover:bg-red-700 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status.type === 'loading' ? 'Sending...' : '🚨 Submit Emergency Request'}
                </button>

                <p className="text-center text-sm text-gray-600">
                  For immediate assistance, please call <a href="tel:+447379787766" className="text-red-600 font-bold">07379 787766</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Emergency Call-Out Rates</h2>
            <p className="text-lg text-gray-600">Transparent pricing for emergency services</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {Object.entries(emergencyPricing).map(([key, pricing], index) => (
              <div
                key={key}
                className={`card p-6 text-center border-2 animate-fade-in-up ${
                  key === 'emergency' ? 'border-red-500 bg-red-50' : 'border-gray-200'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-2 capitalize">{key}</h3>
                <p className="text-sm text-gray-600 mb-4">{pricing.desc}</p>
                <div className="text-3xl font-bold text-primary-600 mb-2">{pricing.rate}</div>
                <p className="text-sm text-gray-500">Zone 1: {pricing.zone1}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-8 text-gray-600 max-w-2xl mx-auto">
            * Rates are approximate and may vary based on the complexity of the job. 
            All prices include callout charge. No hidden fees.
          </p>
        </div>
      </section>

      {/* Why Choose Our Emergency Service */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center animate-fade-in-up">
              Why Choose Our Emergency Service?
            </h2>
            <div className="space-y-6">
              {[
                { icon: '⚡', title: 'Rapid Response', desc: 'Engineers on call 24/7 with average response time under 60 minutes' },
                { icon: '🎓', title: 'Qualified Experts', desc: 'Gas Safe registered engineers with emergency training' },
                { icon: '🚗', title: 'Fully Equipped', desc: 'Vans stocked with parts for most common emergencies' },
                { icon: '💰', title: 'Fixed Rates', desc: 'Clear pricing with no surprise charges' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <span className="text-4xl flex-shrink-0">{item.icon}</span>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success/Error Popup */}
      {showPopup && status.message && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 animate-fade-in"
            onClick={() => setShowPopup(false)}
          ></div>
          
          {/* Popup */}
          <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full animate-scale-in z-10">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
            
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl ${
                status.type === 'success' 
                  ? 'bg-green-100 text-green-600' 
                  : 'bg-red-100 text-red-600'
              }`}>
                {status.type === 'success' ? '✓' : '✗'}
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {status.type === 'success' ? 'Success!' : 'Oops!'}
              </h3>
              
              <p className="text-gray-600 mb-6">
                {status.message}
              </p>
              
              <button
                onClick={() => setShowPopup(false)}
                className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 ${
                  status.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emergency;
